import { useRef, useState } from "react";
import { parseFile } from "../../features/data/FileParser";
import { analyzeData } from "../../features/data/analyzer";
import { buildDatasetSchema } from "../../features/data/datasetSchema";
import { buildInsights } from "../../features/dashboard/insightBuilder";
import { buildDashboard } from "../../features/dashboard/dashboardEngine";
import { suggestInitialQuestions } from "../../features/ai/followUpSuggester";
import { generateSampleDataset, SAMPLE_DATASET_FILENAME } from "../../data/sampleDataset";

import { useAuth } from "../../context/AuthContext";
import { useSubscription } from "../../context/SubscriptionContext";
import { createSession } from "../../firebase/firestore";
import Logo from "../common/Logo";

const ACCEPTED_EXTENSIONS = ["csv", "xlsx", "xls"];

const STAGE_LABELS = {
  reading: "Reading your dataset...",
  profiling: "Profiling columns...",
  insights: "Detecting insights...",
  preparing: "Preparing your workspace...",
  saving: "Saving your dashboard...",
};

function getExtension(filename = "") {
  return filename.split(".").pop().toLowerCase();
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileUpload({
  setSessions,
  setActiveSession,
  onOpenSubscription,
}) {

  const { user } = useAuth();
  const { capabilities, plan } = useSubscription();

  const [dragging, setDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null); // { name, size }
  const [stage, setStage] = useState(null); // null | one of STAGE_LABELS keys
  const [error, setError] = useState(null); // { message, upgrade?: boolean }

  const inputRef = useRef(null);

  const reset = () => {
    setSelectedFile(null);
    setStage(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  // -----------------------------
  // VALIDATION
  // -----------------------------
  const validateFile = (file) => {

    const ext = getExtension(file.name);

    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return { message: "Please upload a CSV or Excel file." };
    }

    const maxBytes = capabilities.maxFileSizeMB * 1024 * 1024;

    if (file.size > maxBytes) {
      return {
        message: `This file exceeds the ${capabilities.maxFileSizeMB} MB limit for your current plan.`,
        upgrade: plan === "free",
      };
    }

    return null;
  };

  // -----------------------------
  // SHARED PIPELINE
  // -----------------------------
  // Runs the exact same profiling/insight/dashboard/session-creation
  // pipeline for any in-memory dataset, whether it came from a parsed
  // upload or the generated sample dataset — so "try with sample data"
  // is a real product session, not a separate demo path.
  const runPipeline = async (dataset, fileName) => {

    if (!dataset?.length) {
      throw { message: "This file does not contain usable data." };
    }

    if (dataset.length > capabilities.maxRows) {
      throw {
        message: `This file has more rows than your current plan supports (max ${capabilities.maxRows.toLocaleString()}).`,
        upgrade: plan === "free",
      };
    }

    // ---- Profiling columns ----
    setStage("profiling");
    const analysis = analyzeData(dataset);
    const schema = buildDatasetSchema(dataset);

    // ---- Detecting insights ----
    setStage("insights");
    const insights = buildInsights(dataset, schema);
    const dashboard = buildDashboard(dataset, schema);

    // ---- Preparing workspace (suggested questions — schema only, no rows) ----
    setStage("preparing");
    const suggestedQuestions = await suggestInitialQuestions(schema);

    // ---- Saving ----
    setStage("saving");

    const sessionData = {
      uid: user.uid,
      name: fileName,
      dataset,
      analysis,
      schema,
      insights,
      dashboard,
      suggestedQuestions,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      messages: [
        {
          role: "assistant",
          text: `Dataset loaded successfully.\n\nI analyzed ${analysis.rows} records across ${analysis.columnCount} columns and I'm ready to answer questions, generate insights, and help explore your data.\n\nWhat would you like to know?`,
        },
      ],
    };

    const firebaseId = await createSession(user.uid, sessionData);

    const newSession = { firebaseId, ...sessionData };

    setSessions((prev) => [newSession, ...prev]);
    setActiveSession(newSession);
  };

  // -----------------------------
  // PROCESS FILE
  // -----------------------------
  const processFile = async (file) => {

    if (!user) {
      setError({ message: "Please sign in first." });
      return;
    }

    const validationError = validateFile(file);

    if (validationError) {
      setSelectedFile({ name: file.name, size: file.size });
      setError(validationError);
      return;
    }

    setSelectedFile({ name: file.name, size: file.size });
    setError(null);

    try {

      // ---- Reading dataset ----
      setStage("reading");

      let dataset;

      try {
        dataset = await parseFile(file);
      } catch (parseErr) {
        console.error("File parse failed:", parseErr);
        throw { message: "We couldn't read this file. Please check that it is a valid CSV or Excel file." };
      }

      await runPipeline(dataset, file.name);

    } catch (err) {

      console.error("Upload failed:", err);

      setError(
        err?.message
          ? err
          : { message: "Something went wrong while processing this file. Please try again." }
      );
      setStage(null);
    }
  };

  // -----------------------------
  // TRY WITH SAMPLE DATA
  // -----------------------------
  // Generates a realistic sample dataset in memory and runs it through
  // the exact same pipeline as a real upload — same profiling, same
  // insights/dashboard generation, same Firestore session, same
  // subscription/usage limits on the questions asked afterward.
  const loadSampleData = async () => {

    if (!user) {
      setError({ message: "Please sign in first." });
      return;
    }

    if (isBusy) return;

    setSelectedFile({ name: SAMPLE_DATASET_FILENAME, size: 0 });
    setError(null);

    try {
      const dataset = generateSampleDataset();
      await runPipeline(dataset, SAMPLE_DATASET_FILENAME);
    } catch (err) {

      console.error("Failed to load sample dataset:", err);

      setError(
        err?.message
          ? err
          : { message: "Something went wrong while loading the sample dataset. Please try again." }
      );
      setStage(null);
    }
  };

  // -----------------------------
  // HANDLERS
  // -----------------------------
  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!stage) setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const isBusy = !!stage;
  const isError = !!error;

  return (
    <div className="w-full flex justify-center pt-16 pb-10 lg:py-10 px-4 sm:px-0">
      <div className="w-full max-w-2xl">

        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Upload your dataset</h1>
          <p className="text-gray-500 text-sm mt-1.5">
            Upload a CSV or Excel file to start analyzing your data with InsightPulse AI.
          </p>
        </div>

        {/* DROPZONE */}
        <label
          htmlFor="dataset-upload"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            flex flex-col items-center justify-center
            w-full min-h-[240px] sm:min-h-[260px]
            rounded-3xl border-2 border-dashed
            transition-all duration-200
            shadow-xl px-6 py-8
            ${isBusy ? "cursor-not-allowed opacity-90" : "cursor-pointer"}
            ${dragging
              ? "border-blue-500 bg-blue-950/20"
              : isError
              ? "border-red-800/60 bg-red-950/10"
              : "border-gray-700 bg-gray-900 hover:bg-gray-850 hover:border-blue-500/70"
            }
          `}
        >

          {/* PROCESSING STATE */}
          {isBusy && (
            <>
              <div className="w-10 h-10 border-4 border-gray-700 border-t-blue-500 rounded-full animate-spin mb-5" />
              <h2 className="text-lg font-semibold text-white text-center">{STAGE_LABELS[stage]}</h2>
              <p className="mt-2 text-gray-500 text-sm text-center truncate max-w-full">{selectedFile?.name}</p>
            </>
          )}

          {/* ERROR STATE */}
          {!isBusy && isError && (
            <>
              <div className="w-14 h-14 rounded-full bg-red-950/50 border border-red-800/60 flex items-center justify-center text-2xl mb-4">
                ⚠️
              </div>
              <h2 className="text-lg font-semibold text-red-300 text-center">Unable to process this file</h2>
              <p className="mt-2 text-gray-400 text-sm text-center max-w-md">{error.message}</p>

              <div className="mt-5 flex items-center gap-3">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); reset(); }}
                  className="px-4 py-2 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 transition text-sm font-medium"
                >
                  Choose Different File
                </button>

                {error.upgrade && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); onOpenSubscription?.(); }}
                    className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 transition text-sm font-semibold"
                  >
                    Upgrade to Premium
                  </button>
                )}
              </div>
            </>
          )}

          {/* IDLE / EMPTY STATE */}
          {!isBusy && !isError && (
            <>
              <div className="mb-4">
                <Logo size={56} />
              </div>

              <h2 className="text-xl font-bold text-white text-center">
                Drop your CSV or Excel file here
              </h2>

              <p className="mt-2 text-gray-500 text-sm text-center">
                or click anywhere in this box to browse
              </p>

              <div className="mt-6 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 transition font-semibold text-white text-sm">
                Choose File
              </div>

              <div className="mt-6 flex items-center gap-2">
                {ACCEPTED_EXTENSIONS.map((ext) => (
                  <span key={ext} className="text-[11px] uppercase font-medium bg-gray-800 text-gray-400 px-2 py-1 rounded-md">
                    {ext}
                  </span>
                ))}
              </div>

              <p className="mt-3 text-[11px] text-gray-600">
                Maximum file size: {capabilities.maxFileSizeMB} MB
                {plan === "free" && " — higher limits available with Premium"}
              </p>
            </>
          )}

          <input
            id="dataset-upload"
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleInputChange}
            disabled={isBusy}
            className="hidden"
          />
        </label>

        {/* TRY WITH SAMPLE DATA */}
        {!isBusy && !isError && (
          <div className="mt-5 flex items-center justify-center gap-3 text-xs text-gray-600">
            <span className="h-px flex-1 bg-gray-800" />
            <span>or</span>
            <span className="h-px flex-1 bg-gray-800" />
          </div>
        )}

        {!isBusy && !isError && (
          <button
            type="button"
            onClick={loadSampleData}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-900 hover:bg-gray-800 border border-gray-800 hover:border-gray-700 transition text-sm font-medium text-gray-300"
          >
            <span aria-hidden="true">🧪</span>
            Try with sample data
            <span className="text-gray-600 font-normal">— no file needed</span>
          </button>
        )}

        {/* SELECTED FILE INFO (shown while processing, above any error) */}
        {selectedFile && isBusy && (
          <div className="mt-4 flex items-center justify-between bg-gray-900/60 border border-gray-800 rounded-xl px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-lg shrink-0">📄</span>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{selectedFile.name}</div>
                <div className="text-xs text-gray-500">
                  {selectedFile.size > 0 ? formatFileSize(selectedFile.size) : "Generated sample dataset"}
                </div>
              </div>
            </div>
            <span className="text-xs text-blue-400 shrink-0">Processing...</span>
          </div>
        )}

      </div>
    </div>
  );
}
